import { CheckboxGroup } from '@televet/kibble-ui/CheckboxGroup';
import { Select } from '@televet/kibble-ui/Select';
import { VStack } from '@televet/kibble-ui/chakra';
import { Controller, useWatch, type Control } from 'react-hook-form';
import {
  AUDIENCE_MODE_LABELS,
  PIMS_SYSTEM_OPTIONS,
  isAudienceMode,
  toPimsSystems,
  type AudienceFormValues,
} from './audience';

const AUDIENCE_MODE_OPTIONS = Object.entries(AUDIENCE_MODE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const PIMS_CHECKBOXES = PIMS_SYSTEM_OPTIONS.map(({ value, label }) => ({
  value,
  label,
  'data-testid': `prompt-template-audience-pims-${value}`,
}));

export const AudienceField = ({
  control,
}: {
  control: Control<AudienceFormValues>;
}): React.JSX.Element => {
  const audienceMode = useWatch({ control, name: 'audienceMode' });

  return (
    <VStack gap={3} align="stretch">
      <Controller
        name="audienceMode"
        control={control}
        render={({ field }) => (
          <Select
            label="Audience"
            data-testid="prompt-template-audience-mode"
            helperText="Which clinics see this template, by their PIMS. A clinic with no known PIMS only sees templates set to Everyone."
            options={AUDIENCE_MODE_OPTIONS}
            value={[field.value]}
            onValueChange={(details) => {
              const mode = details.value[0];
              if (mode && isAudienceMode(mode)) field.onChange(mode);
            }}
          />
        )}
      />
      {audienceMode !== 'everyone' && (
        <Controller
          name="audienceSystems"
          control={control}
          rules={{
            validate: (systems, values) =>
              values.audienceMode === 'everyone' || systems.length > 0 || 'Pick at least one PIMS',
          }}
          render={({ field, fieldState }) => (
            <CheckboxGroup
              label="PIMS"
              data-testid="prompt-template-audience-pims"
              direction="row"
              flexWrap="wrap"
              gap={4}
              checkboxes={PIMS_CHECKBOXES}
              value={field.value}
              onValueChange={(values) => field.onChange(toPimsSystems(values))}
              invalid={!!fieldState.error}
              errorText={fieldState.error?.message}
            />
          )}
        />
      )}
    </VStack>
  );
};
